import {
  AmountColumn,
  Column,
  DateColumn,
  DateTimeColumn,
  EntityColumn,
  GroupColumn,
  isServerSideColumn,
  NumberColumn,
  ObjectColumn,
  QuantityColumn,
  ServerSideColumn,
  StringColumn
} from '../types/grid';
import { ColDef } from 'ag-grid-enterprise';
import { ColGroupDef } from 'ag-grid-community/dist/types/src/entities/colDef';
import { isSignal, Signal } from '@angular/core';
import { isDefined, isDefinedNotEmpty } from '@softline/core';
import { FormatOptions, Formatter } from '@softline/ui-core';

export function toAgGridColDefs(columns: Column<any>[],
                                formatter: Formatter,
                                loadFilterCallback: (col: ServerSideColumn) => Promise<unknown[]>): (ColDef | ColGroupDef)[] {
  const traverse = [...columns];
  const returnValue: (ColDef | ColGroupDef)[] = []
  while (traverse.length > 0) {
    const col = traverse.shift();
    if (!col)
      continue;
    switch (col.type) {
      case 'group':
        returnValue.push(toGroupColDef(col, formatter, loadFilterCallback));
        break;
      case 'string':
        returnValue.push(toStringColDef(col, formatter));
        break;
      case 'number':
        returnValue.push(toNumberColDef(col, formatter));
        break;
      case 'date':
        returnValue.push(toDateColDef(col, formatter));
        break;
      case 'dateTime':
        returnValue.push(toDateTimeColDef(col, formatter));
        break;
      case 'object':
        returnValue.push(toObjectColDef(col, formatter));
        break;
      case 'amount':
        returnValue.push(toAmountColDef(col, formatter));
        break;
      case 'quantity':
        returnValue.push(toQuantityColDef(col, formatter));
        break;
      case 'entity':
        returnValue.push(toEntityColDef(col, formatter, loadFilterCallback));
        break;
    }
  }
  return returnValue;
}

function toGroupColDef(col: GroupColumn<any>, formatter: Formatter, loadFilterCallback: (col: ServerSideColumn) => Promise<unknown[]>): ColGroupDef {
  return {
    headerName: col.header(),
    children: toAgGridColDefs(col.children, formatter, loadFilterCallback),
  }
}

function toStringColDef(col: StringColumn<any>, formatter: Formatter): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => isDefined(p?.data) ? col.getValue(p.data) : undefined;
  let colDef: ColDef = {
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    filter: 'agTextColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter
    },
    headerClass: col.headerClass,
    cellClass: col.cellClass,
    field: isServerSideColumn(col) ? col.serverSideField : undefined,
    aggFunc: col.aggFunc,
    allowedAggFuncs: ['count', 'concat', 'first', 'last', 'single'],
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    hide: col.hide,
    cellRenderer: col.cellRenderer,
    valueFormatter,
    keyCreator: valueFormatter,
  }
  return colDef;
}

function toNumberColDef(col: NumberColumn<any>, formatter: Formatter): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => isDefined(p?.data) ? col.getValue(p.data) : undefined;
  let colDef = {
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    filter: 'agNumberColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter
    },
    headerClass: col.headerClass,
    cellClass: (col.cellClass ?? '') + ' text-right',
    field: isServerSideColumn(col) ? col.serverSideField : undefined,
    aggFunc: col.aggFunc,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    hide: col.hide,
    cellRenderer: col.cellRenderer,
    valueFormatter,
    keyCreator: valueFormatter,
  }
  return colDef;
}

function toDateColDef(col: DateColumn<any>, formatter: Formatter): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => isDefined(p?.data) ? col.getValue(p.data) : undefined;
  let colDef = {
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    filter: 'agDateColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter
    },
    headerClass: col.headerClass,
    cellClass: col.cellClass,
    field: isServerSideColumn(col) ? col.serverSideField : undefined,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    hide: col.hide,
    cellRenderer: col.cellRenderer,
    valueFormatter,
    keyCreator: valueFormatter,
  }
  return colDef;
}

function toDateTimeColDef(col: DateTimeColumn<any, any>, formatter: Formatter): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => isDefined(p?.data) ? col.getValue(p.data) : undefined;
  let colDef = {
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    filter: 'agDateColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter
    },
    headerClass: col.headerClass,
    cellClass: col.cellClass,
    field: isServerSideColumn(col) ? col.serverSideField : undefined,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    hide: col.hide,
    cellRenderer: col.cellRenderer,
    valueFormatter,
    keyCreator: valueFormatter,
  }
  return colDef;
}

function toAmountColDef(col: AmountColumn<any>, formatter: Formatter): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const amountFormatter = (v: any) => {
    if(!isDefined(v) || !valueFormatter)
      return '';
    const currency = col.getCurrency(v.data);
    if(!currency)
      return ''
    //TODO: Mutliple currencies for Grouping
    return valueFormatter(v, {currency});
  }
  const valueGetter = (p) => isDefined(p?.data) ? col.getValue(p.data) : undefined;
  let colDef = {
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    filter: 'agNumberColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter
    },
    cellClass: (col.cellClass ?? '') + ' text-right',
    field: isServerSideColumn(col) ? col.serverSideField : undefined,
    aggFunc: col.aggFunc,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    hide: col.hide,
    cellRenderer: col.cellRenderer,
    valueFormatter: amountFormatter,
    keyCreator: valueFormatter,
  }
  return colDef;
}

function toQuantityColDef(col: QuantityColumn<any>, formatter: Formatter): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const amountFormatter = (v: any) => {
    if(!isDefined(v) || !valueFormatter)
      return '';
    const unit = col.getUnit(v.data);
    if(!unit)
      return ''
    //TODO: Mutliple currencies for Grouping
    const value = valueFormatter(v);
    return isDefinedNotEmpty(value) ? `${value} ${unit}` : '';
  }
  const valueGetter = (p) => isDefined(p?.data) ? col.getValue(p.data) : undefined;
  let colDef = {
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    filter: 'agNumberColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter
    },
    cellClass: (col.cellClass ?? '') + ' text-right',
    field: isServerSideColumn(col) ? col.serverSideField : undefined,
    aggFunc: col.aggFunc,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    hide: col.hide,
    cellRenderer: col.cellRenderer,
    valueFormatter: amountFormatter,
    keyCreator: valueFormatter,
  }
  return colDef;
}

function toObjectColDef(col: ObjectColumn<any, any>, formatter: Formatter): ColDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => isDefined(p?.data) ? col.getValue(p.data) : undefined;
  let colDef = {
    colId: col.colId,
    headerName: col.header(),
    valueGetter: valueGetter,
    filter: 'agSetColumnFilter',
    filterValueGetter: valueGetter,
    filterParams: {
      valueFormatter
    },
    headerClass: col.headerClass,
    cellClass: col.cellClass,
    field: isServerSideColumn(col) ? col.serverSideField : undefined,
    enableRowGroup: col.enableRowGroup ?? true,
    rowGroup: col.rowGroup,
    hide: col.hide,
    valueFormatter,
    keyCreator: valueFormatter,
    comparator: col.sortBy ? (a, b, n1, n2, isDescending) => {
      if(!col.sortBy)
        return 0
      const aVal = col.sortBy(a);
      const bVal = col.sortBy(b);
      return aVal === bVal ? 0 : (aVal < bVal ? -1 : 1);
    } : undefined,
  }
  return colDef;
}

function toEntityColDef(col: EntityColumn<any, any>, formatter: Formatter, loadFilterCallback: (col: ServerSideColumn) => Promise<unknown[]>): ColGroupDef {
  const valueFormatter = getValueFormatter(col, formatter);
  const valueGetter = (p) => isDefined(p?.data) ? col.getValue(p.data) : undefined;

  const filterParams: any = isServerSideColumn(col) ? {
        values: async (params) => {
          const result = await loadFilterCallback(col);
          params.success(result);
        },
        keyCreator: o => o.value.id,
        valueFormatter: valueFormatter,
        refreshValuesOnOpen: true,
    } : { valueFormatter };

  let colDef: ColGroupDef = {
    headerName: col.header(),
    marryChildren: true,
    headerClass: col.headerClass ?? undefined,
    children: [{
      colId: col.colId,
      headerName: col.header(),
      valueGetter,
      filter: 'agSetColumnFilter',
      filterValueGetter: valueGetter,
      filterParams: filterParams,
      enableRowGroup: col.enableRowGroup ?? true,
      rowGroup: col.rowGroup,
      hide: col.hide,
      cellRenderer: col.cellRenderer,
      minWidth: 400,
      valueFormatter,
      keyCreator: valueFormatter,
      comparator: col.sortBy ? (a, b, n1, n2, isDescending) => {
        if(!col.sortBy)
          return 0
        const aVal = col.sortBy(a);
        const bVal = col.sortBy(b);
        return aVal === bVal ? 0 : (aVal < bVal ? -1 : 1);
      } : undefined,
      columnGroupShow: 'closed',
      },
      ...toAgGridColDefs(col.children, formatter, loadFilterCallback)
        .map(o => {
          if(isColGroupDef(o))
            return o
          return {
            ...o,
            colId: `${col.colId}.${o.colId}`,
            valueGetter: (p) => (o.valueGetter && typeof o.valueGetter !== 'string') ? o.valueGetter({...p, data: valueGetter({data: p.data})}) : undefined,
            field: isServerSideColumn(col) ? `${col.serverSideField}.${o.field}` : undefined,
            hide: col.hide,
            columnGroupShow: 'open' as 'open',
          }
        }),
    ],
  }
  return colDef;
}

function getValueFormatter(col: { format?: string | ((v: any) => string | Signal<string>)}, formatter: Formatter): ((v: any, formatOptions?: FormatOptions) => string) | undefined {
  if(!col.format)
    return undefined;
  return (v: any, formatOptions?: FormatOptions) => {
    if(!isDefined(v?.value))
      return undefined;
    if(!col.format)
      return v.value;
    const formatted = typeof col.format === 'string'
      ? formatter(v.value, col.format, formatOptions)
      : col.format(v.value)
    if(isSignal(formatted))
      return formatted();
    return formatted;
  }
}

function isColGroupDef(col: ColDef | ColGroupDef): col is ColGroupDef {
  return (col as ColGroupDef).children !== undefined;
}
